import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { translations, Language, TranslationKey } from '../i18n/translations';

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: (key: TranslationKey) => string;
    isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
    const [language, setLanguageState] = useState<Language>(() => {
        // Load language from sessionStorage or localStorage, or default to 'en'
        const savedSession = sessionStorage.getItem('language');
        if (savedSession === 'ar' || savedSession === 'en') {
            return savedSession;
        }

        const savedLocal = localStorage.getItem('language');
        if (savedLocal === 'ar' || savedLocal === 'en') {
            sessionStorage.setItem('language', savedLocal);
            localStorage.removeItem('language');
            return savedLocal;
        }

        return 'en';
    });

    const isRTL = language === 'ar';

    // Apply RTL to document when language changes
    useEffect(() => {
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.documentElement.lang = language;
    }, [isRTL, language]);

    const setLanguage = (lang: Language) => {
        setLanguageState(lang);
        sessionStorage.setItem('language', lang);
        localStorage.removeItem('language');
    };

    const t = (key: TranslationKey): string => {
        return translations[language][key] || key;
    };

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t, isRTL }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within LanguageProvider');
    }
    return context;
}
